import domReady from '@wordpress/dom-ready';

const DESKTOP_BREAKPOINT_PX = 768;
const SCROLLED_THRESHOLD_PX = 10;
const OVERLAY_FOCUS_DELAY_MS = 100;
// Breathing room kept between the desktop panel and the header's edges.
const PANEL_EDGE_GUTTER_PX = 24;
// Keeps the tail from sliding past the panel's own rounded corners.
const PANEL_TAIL_MIN_INSET_PX = 16;
// Multiplier for taking the midpoint of a measured box.
const CENTRE_RATIO = 0.5;

class Header {
  constructor(element) {
    this.element = element;
    this.isOpen = false;

    this.desktopLayout = this.element.querySelector(
      '.header-desktop-layout'
    );
    this.toggleButton = this.element.querySelector('[data-nav-toggle]');
    this.overlay = this.element.querySelector('[data-nav-overlay]');
    this.desktopLinks = this.element.querySelectorAll(
      '.nav-menu-desktop .nav-menu-list li > a, .nav-menu-desktop .nav-menu-list li > .nav-panel-trigger'
    );
    this.desktopPanelItems = this.element.querySelectorAll(
      '.nav-menu-desktop .nav-menu-list > li.menu-item-has-children'
    );
    this.desktopTopLevelLinksWithoutChildren =
			this.element.querySelectorAll(
			  '.nav-menu-desktop .nav-menu-list > li:not(.menu-item-has-children) > a'
			);

    this.mobileTopLevelLinksWithChildren = this.element.querySelectorAll(
      'div.nav-menu-mobile>ul>li.menu-item.menu-item-has-children>a'
    );
    this.mobileLevel2Links = this.element.querySelectorAll(
      'div.nav-menu-mobile>ul>li.menu-item.menu-item-has-children>ul.sub-menu>li.menu-item>a'
    );

    this.init();
  }

  init() {
    if (!this.toggleButton || !this.overlay) {
      return;
    }

    this.setupAttributes();
    this.bindEvents();
    this.updateNavHeight();
    this.shouldRenderDesktopLayout();
    this.handleScroll();
    this.positionPanels();
  }

  // Centre each panel on the item that opens it, then clamp it inside the header.
  // Centring on the header alone reads as detached from the trigger; clamping is
  // what guarantees the panel cannot cross a viewport edge at any width.
  positionPanels() {
    if (!this.desktopLayout) {
      return;
    }

    const headerRect = this.desktopLayout.getBoundingClientRect();
    if (!headerRect.width) {
      return;
    }

    this.desktopLayout.style.setProperty(
      '--nav-panel-max-inline-size',
      `${headerRect.width - PANEL_EDGE_GUTTER_PX - PANEL_EDGE_GUTTER_PX}px`
    );

    this.desktopPanelItems.forEach((item) => {
      const panel = item.querySelector('.nav-panel');
      if (!panel) {
        return;
      }

      const itemRect = item.getBoundingClientRect();
      const panelWidth = panel.offsetWidth;
      const isRtl = getComputedStyle(item).direction === 'rtl';

      // Work in "inline-start" space so the clamp holds in both directions.
      const startEdge = (rect) => (isRtl ? headerRect.right - rect.right : rect.left - headerRect.left);
      const itemStart = startEdge(itemRect);
      const itemCentre = itemStart + itemRect.width * CENTRE_RATIO;

      const ideal = itemCentre - panelWidth * CENTRE_RATIO;
      const maxStart = headerRect.width - panelWidth - PANEL_EDGE_GUTTER_PX;
      const clamped = Math.max(PANEL_EDGE_GUTTER_PX, Math.min(ideal, maxStart));

      panel.style.setProperty('--nav-panel-inline-start', `${clamped - itemStart}px`);
      panel.style.setProperty(
        '--nav-panel-tail-offset',
        `${Math.max(
          PANEL_TAIL_MIN_INSET_PX,
          Math.min(itemCentre - clamped, panelWidth - PANEL_TAIL_MIN_INSET_PX)
        )}px`
      );
    });
  }

  activatePane(railItem) {
    const panel = railItem.closest('.nav-panel');
    const listItem = railItem.closest('li');
    if (!panel || !listItem) {
      return;
    }

    panel.querySelectorAll('.nav-panel-rail > li').forEach((li) => {
      const isActive = li === listItem;
      li.classList.toggle('is-active', isActive);
      const trigger = li.querySelector('.rail-item-parent');
      if (trigger) {
        trigger.setAttribute('aria-expanded', isActive.toString());
      }
    });

    const paneId = railItem.getAttribute('aria-controls');
    panel.querySelectorAll('.nav-panel-pane').forEach((pane) => {
      pane.hidden = pane.id !== paneId;
      pane.classList.toggle('is-active', pane.id === paneId);
    });
  }

  openPanel(item) {
    this.closeOtherDesktopMenus(item);
    item.classList.add('active');
    const trigger = item.querySelector('.nav-panel-trigger');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }
    this.positionPanels();
  }

  setupAttributes() {
    this.desktopLinks.forEach((link) => {
      link.setAttribute('data-text', `${link.textContent}v`);
    });
  }

  bindEvents() {
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.element);
    if (this.desktopLayout) {
      this.resizeObserver.observe(this.desktopLayout);
    }

    window.addEventListener('scroll', () => this.handleScroll(), {
      passive: true,
    });

    // window.addEventListener('hashchange', () => {
    //   if(this.isOpen) this.toggleMenu();
    // })

    this.overlay.addEventListener('click', () => {
      if (this.isOpen) {this.toggleMenu();}
    });

    this.toggleButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleMenu();
    });

    this.toggleButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      }
    });

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.toggleMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') {
        return;
      }
      if (this.isOpen) {
        this.toggleMenu();
        this.toggleButton.focus();
        return;
      }
      const openItem = this.element.querySelector(
        '.nav-menu-desktop .nav-menu-list > li.menu-item-has-children.active'
      );
      if (openItem) {
        this.closeOtherDesktopMenus(null);
        openItem.querySelector('.nav-panel-trigger')?.focus();
      }
    });

    document.addEventListener('click', (e) => this.handleOutsideClick(e));

    this.mobileLevel2Links.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        this.handleMobileLevel2Click(e, link);
      });
    });

    this.desktopPanelItems.forEach((item) => {
      item.addEventListener('mouseenter', () => this.openPanel(item));

      const trigger = item.querySelector('.nav-panel-trigger');
      if (trigger) {
        // Deliberately no focus-to-open: it reopened the panel that Escape had
        // just closed, and popped panels open while tabbing past them. Enter and
        // Space reach the click handler, which is the disclosure pattern anyway.
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = item.classList.contains('active');
          if (isOpen) {
            this.closeOtherDesktopMenus(null);
          } else {
            this.openPanel(item);
          }
        });
      }

      item.querySelectorAll('.rail-item').forEach((railItem) => {
        railItem.addEventListener('mouseenter', () => this.activatePane(railItem));
        railItem.addEventListener('focus', () => this.activatePane(railItem));
      });
    });

    this.desktopTopLevelLinksWithoutChildren.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        this.closeOtherDesktopMenus(null);
      });
    });

    //Mobile specific logic
    this.mobileTopLevelLinksWithChildren.forEach((link) => {
      const parent = link.parentElement;
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleMobileTopLevelClick(e, parent);
      });
    });
  }

  updateNavHeight() {
    const navHeight = this.element.children[0].offsetHeight;
    document.body.style.setProperty('--nav-height', `${navHeight}px`);
  }

  shouldRenderDesktopLayout() {
    if (this.desktopLayout) {
      const doesDesktopFit =
				this.desktopLayout.scrollWidth <=
				this.desktopLayout.clientWidth;
      const isViewportDesktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;

      this.element.setAttribute(
        'data-desktop-layout-available',
        doesDesktopFit && isViewportDesktop
      );
    } else {
      this.element.setAttribute('data-desktop-layout-available', 'false');
    }
  }

  handleResize() {
    if (window.innerWidth >= DESKTOP_BREAKPOINT_PX && this.isOpen) {
      this.toggleMenu();
    }
    this.updateNavHeight();
    this.shouldRenderDesktopLayout();
    this.positionPanels();
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    this.element.classList.toggle('scrolled', currentScrollY > SCROLLED_THRESHOLD_PX);
  }

  toggleMenu() {
    this.isOpen = !this.isOpen;

    this.toggleButton.classList.toggle('active', this.isOpen);
    this.toggleButton.setAttribute('aria-expanded', this.isOpen.toString());
    this.toggleButton.setAttribute(
      'aria-label',
      this.isOpen ? 'Cerrar menú' : 'Abrir menú'
    );

    this.overlay.classList.toggle('active', this.isOpen);
    document.body.classList.toggle('nav-menu-open', this.isOpen);

    if (this.isOpen) {
      const firstFocusable = this.overlay.querySelector(
        'a, button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), OVERLAY_FOCUS_DELAY_MS);
      }
    }
  }

  handleMobileTopLevelClick(e, parent) {
    e.preventDefault();
    this.closeOtherMobileMenus(parent);
    parent.classList.toggle('active');
  }

  closeOtherMobileMenus(currentParent) {
    this.mobileTopLevelLinksWithChildren.forEach((item) => {
      if (item.parentElement !== currentParent) {
        item.parentElement.classList.remove('active');
      }
    });
  }

  handleMobileLevel2Click(e, link) {
    const parentLi = link.parentElement;
    const subMenu = parentLi.querySelector('ul.sub-menu');
    const level3Link = subMenu ? subMenu.querySelector('li > a') : null;

    if (level3Link) {
      e.preventDefault();
      window.location.href = level3Link.href;
    } else {
      window.location.href = link.href;
    }
  }

  // handleDesktopTopLevelClick(e, parent) {
  // e.preventDefault();
  // this.closeOtherDesktopMenus(parent);
  // parent.classList.toggle('active');
  // }

  closeOtherDesktopMenus(currentItem) {
    this.desktopPanelItems.forEach((item) => {
      if (item === currentItem) {
        return;
      }
      item.classList.remove('active');
      const trigger = item.querySelector('.nav-panel-trigger');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  handleOutsideClick(e) {
    if (
      !e.target.closest(
        '.nav-menu-desktop .nav-menu-list > li.menu-item-has-children'
      )
    ) {
      this.closeOtherDesktopMenus(null);
    }
  }
}

domReady(() => {
  const navBars = document.querySelectorAll('[data-nav-bar]');
  navBars.forEach((navBar) => new Header(navBar));
});
